import {createContext, Dispatch, FC, PropsWithChildren, SetStateAction, useContext, useMemo, useRef, useState} from "react";
import {SocketContext} from "./socketContext";
import {useSSTranslation} from "@/i18n/useSSTranslation";
import useCustomSnackbar from "@/snackbars/useCustomSnackbar";
import {SnackbarKey} from "notistack";

export enum BulkActionStatus {
	none = "none",
	inProgress = "inProgress",
	complete = "complete"
}

type BulkActionStatusType = keyof typeof BulkActionStatus;

export interface ManageBulkActionStatusValue {
	rescheduleOverdueModules : {
		rescheduleOverdueStatus: BulkActionStatusType;
		setRescheduleOverdueStatus: Dispatch<SetStateAction<BulkActionStatusType>>;
		updateRescheduleOverdueSnackbarId: (id: SnackbarKey | null) => void;
	},
	courseEnrollment: {
		courseEnrollmentStatus: BulkActionStatusType;
		setCourseEnrollmentStatus: Dispatch<SetStateAction<BulkActionStatusType>>;
		updateCourseEnrollmentSnackbarId: (id: SnackbarKey | null) => void;
	},
	playlistEnrollment: {
		playlistEnrollmentStatus: BulkActionStatusType;
		setPlaylistEnrollmentStatus: Dispatch<SetStateAction<BulkActionStatusType>>;
		updatePlaylistEnrollmentSnackbarId: (id: SnackbarKey | null) => void;
	}
}

const ManageBulkActionStatusContext = createContext<ManageBulkActionStatusValue | undefined>(undefined);

export const useManageBulkActionStatusContext = () => {
	const context = useContext(ManageBulkActionStatusContext);
	if (!context) {
		throw new Error("useManageBulkActionStatusContext must be used within a ManageBulkActionStatusProvider");
	}
	return context;
}

export const ManageBulkActionStatusProvider: FC<PropsWithChildren> = ({children}) => {
	const {t} = useSSTranslation();
	const {closeSnackbar, successSnackbar, errorSnackbar} = useCustomSnackbar();
	const socket = useContext(SocketContext);
	const [rescheduleOverdueStatus, setRescheduleOverdueStatus] = useState<BulkActionStatusType>(BulkActionStatus.none);
	const rescheduleOverdueSnackbarIdRef = useRef<SnackbarKey | null>(null);
	const updateRescheduleOverdueSnackbarId = (id: SnackbarKey | null) => {
		rescheduleOverdueSnackbarIdRef.current = id;
	}

	const [courseEnrollmentStatus, setCourseEnrollmentStatus] = useState<BulkActionStatusType>(BulkActionStatus.none);
	const courseEnrollmentSnackbarIdRef = useRef<SnackbarKey | null>(null);
	const updateCourseEnrollmentSnackbarId = (id: SnackbarKey | null) => {
		courseEnrollmentSnackbarIdRef.current = id;
	}

	const [playlistEnrollmentStatus, setPlaylistEnrollmentStatus] = useState<BulkActionStatusType>(BulkActionStatus.none);
	const playlistEnrollmentSnackbarIdRef = useRef<SnackbarKey | null>(null);
	const updatePlaylistEnrollmentSnackbarId = (id: SnackbarKey | null) => {
		playlistEnrollmentSnackbarIdRef.current = id;
	}

	const handleRescheduleOverduesCompletion = (e: { data: { isSuccessful: boolean; }}) => {
		const {isSuccessful} = e.data;
		closeSnackbar(rescheduleOverdueSnackbarIdRef.current);
		updateRescheduleOverdueSnackbarId(null);
		setRescheduleOverdueStatus(BulkActionStatus.complete);

		if (isSuccessful){
			successSnackbar(
				t("notifications_reschedule_success", "Lessons rescheduled successfully."),
				true
			);
			return;
		}

		errorSnackbar(
			t("notifications_reschedule_error", "Unable to reschedule lessons. Please try again."),
			true,
			10000
		);
	}

	const handleCourseEnrollmentCompletion = (e: { data: { isSuccessful: boolean; errorMessage?: string; }}) => {
		const {isSuccessful, errorMessage} = e.data;
		closeSnackbar(courseEnrollmentSnackbarIdRef.current);
		updateCourseEnrollmentSnackbarId(null);
		setCourseEnrollmentStatus(BulkActionStatus.complete);

		// Dispatch custom event so enrollment grids can refetch
		window.dispatchEvent(new CustomEvent("courseEnrollmentsCompleted", {
			detail: { inProgress: false, isSuccessful, errorMessage }
		}));

		if (isSuccessful){
			successSnackbar(
				t("notifications_enrollment_success", "Users enrolled successfully."),
				true
			);
			return;
		}

		// Use the specific error message from backend if available, otherwise use generic message
		const message = errorMessage || t("notifications_enrollment_error", "Unable to enroll users. Please try again.");
		errorSnackbar(
			message,
			true,
			10000
		);
	}

	const handlePlaylistEnrollmentCompletion = (e: { data: { isSuccessful: boolean; errorMessage?: string; }}) => {
		const {isSuccessful, errorMessage} = e.data;
		closeSnackbar(playlistEnrollmentSnackbarIdRef.current);
		updatePlaylistEnrollmentSnackbarId(null);
		setPlaylistEnrollmentStatus(BulkActionStatus.complete);

		// Dispatch custom event so enrollment grids can refetch
		window.dispatchEvent(new CustomEvent("playlistEnrollmentsCompleted", {
			detail: { inProgress: false, isSuccessful, errorMessage }
		}));

		if (isSuccessful){
			successSnackbar(
				t("notifications_enrollment_success", "Users enrolled successfully."),
				true
			);
			return;
		}

		// Use the specific error message from backend if available, otherwise use generic message
		const message = errorMessage || t("notifications_enrollment_error", "Unable to enroll users. Please try again.");
		errorSnackbar(
			message,
			true,
			10000
		);
	}

	if (socket) {
		socket.on("connect", () => {
			if (socket.listeners("completedRescheduleOverdueModules").length === 0){
				socket.on("completedRescheduleOverdueModules", handleRescheduleOverduesCompletion);
			}
			if (socket.listeners("completedCourseEnrollments").length === 0){
				socket.on("completedCourseEnrollments", handleCourseEnrollmentCompletion);
			}
			if (socket.listeners("completedPlaylistEnrollments").length === 0){
				socket.on("completedPlaylistEnrollments", handlePlaylistEnrollmentCompletion);
			}
		});
	}

	const contextValue = useMemo(() => ({
		rescheduleOverdueModules: {
			rescheduleOverdueStatus,
			setRescheduleOverdueStatus,
			updateRescheduleOverdueSnackbarId
		},
		courseEnrollment: {
			courseEnrollmentStatus,
			setCourseEnrollmentStatus,
			updateCourseEnrollmentSnackbarId
		},
		playlistEnrollment: {
			playlistEnrollmentStatus,
			setPlaylistEnrollmentStatus,
			updatePlaylistEnrollmentSnackbarId
		}
	}), [rescheduleOverdueStatus, setRescheduleOverdueStatus, courseEnrollmentStatus, setCourseEnrollmentStatus, playlistEnrollmentStatus, setPlaylistEnrollmentStatus]);

	return (
		<ManageBulkActionStatusContext.Provider value={contextValue}>
			{children}
		</ManageBulkActionStatusContext.Provider>
	);
}
